/* Leave + attendance. */
import { dedupeKey } from "../../domain/biometric";
import { evLeaveDecided, evLeaveSubmitted } from "./events";
import { addDays, inclusiveDays, isISODate } from "../../domain/dates";
import type * as T from "../types";
import { E, fmtNum, has, isInt, isNum, str, type Ctx } from "./ctx";
import { dayView, leaveBalanceOf, parsePeriod, periodRows } from "./derived";
import { nextPunchId } from "./ingest";

/** Employees are forced to themselves; others must name an employee. */
export function selfOrBody(c: Ctx, u: T.User, body: { employeeId?: unknown }): number {
  if (c.isEmployee(u)) return c.ownEid(u);
  if (!isInt(body?.employeeId)) throw E.field("employeeId", "required");
  return body.employeeId;
}

export function decide<X extends { id: number; status: string }>(xs: X[], id: number, approve: unknown, what: string, approved: X["status"]): X {
  if (typeof approve !== "boolean") throw E.field("approve", "must be true or false");
  const x = xs.find((v) => v.id === id);
  if (!x) throw E.notFound(what + " not found");
  if (x.status !== "Pending") throw E.conflict(what + " is not pending");
  x.status = approve ? approved : "Rejected";
  return x;
}

export function leaveOps(c: Ctx) {
  return {
    async listLeaves(): Promise<T.Leave[]> {
      const u = c.principal("leave.list");
      const own = c.isEmployee(u) ? c.ownEid(u) : null;
      return c.state.leaves.filter((l) => own == null || l.employeeId === own).sort((a, b) => b.id - a.id);
    },

    async createLeave(input: T.LeaveInput): Promise<T.Leave> {
      const u = c.principal("leave.create");
      const empId = selfOrBody(c, u, input);
      const type = str(input.type), from = str(input.from), to = str(input.to), note = str(input.note), att = str(input.attachmentKey);
      const fe: Record<string, string> = {};
      if (!c.state.leavePolicies.some((p) => p.type === type)) fe.type = "unknown leave type";
      if (!isISODate(from)) fe.from = "valid date required";
      if (!isISODate(to)) fe.to = "valid date required";
      if (!fe.from && !fe.to && to < from) fe.to = "must be on or after from";
      if (att && !c.ownsKey(u, att)) fe.attachmentKey = "invalid file key";
      if (Object.keys(fe).length) throw E.validation(fe);
      const e = c.emp(empId);
      const days = inclusiveDays(from, to);
      if (type === "Annual" && days > leaveBalanceOf(c, e).remaining) throw E.field("days", "exceeds balance");
      const clash = c.state.leaves.find((l) => l.employeeId === empId && (l.status === "Pending" || l.status === "Approved") && l.from <= to && l.to >= from);
      if (clash) throw E.conflict(`overlaps leave ${clash.id} (${clash.from} → ${clash.to})`);
      const id = c.nextId("leaves", 501, () => c.state.leaves.reduce((m, x) => Math.max(m, x.id), 0));
      const l: T.Leave = { id, employeeId: empId, type, from, to, days, status: "Pending", note };
      if (att) l.attachmentKey = att;
      c.state.leaves.push(l);
      c.audit(u, "Requested leave", "Leave", String(id), `${type} ${from} → ${to} for employee ${empId}`);
      evLeaveSubmitted(c, l, u.id);
      return l;
    },

    async decideLeave(id: number, approve: boolean): Promise<T.Leave> {
      const u = c.principal("leave.decide");
      const l = decide(c.state.leaves, id, approve, "leave", "Approved");
      c.audit(u, approve ? "Approved leave" : "Rejected leave", "Leave", String(id), "");
      evLeaveDecided(c, l);
      return l;
    },

    async leaveBalance(employeeId: number): Promise<T.LeaveBalance> {
      const u = c.principal("leave.balance");
      if (c.isEmployee(u) && c.ownEid(u) !== employeeId) throw E.forbidden("employees may only view their own balance");
      return leaveBalanceOf(c, c.emp(employeeId));
    },

    async leavePolicies(): Promise<T.LeavePolicy[]> {
      c.principal("tenant");
      return c.state.leavePolicies;
    },

    async attendanceDay(date?: string): Promise<T.AttendanceDay> {
      c.principal("attendance.day");
      const d = date || c.today;
      if (!isISODate(d)) throw E.field("date", "valid date required");
      return dayView(c, d);
    },

    async attendanceMonth(employeeId: number, period: string): Promise<T.DailyAttendance[]> {
      const u = c.principal("attendance.month");
      parsePeriod(period);
      if (c.isEmployee(u) && c.ownEid(u) !== employeeId) throw E.forbidden("employees may only view their own attendance");
      c.emp(employeeId);
      return periodRows(c, period, [employeeId]);
    },

    async check(req: T.CheckRequest): Promise<T.Punch> {
      const u = c.principal("attendance.check");
      const own = c.ownEid(u);
      let empId = own;
      if (has(req, "employeeId")) {
        if (!isInt(req.employeeId)) throw E.field("employeeId", "must be an integer");
        empId = req.employeeId;
      }
      if (empId !== own) throw E.forbidden("you may only check in or out for yourself");
      if (req.kind !== "in" && req.kind !== "out") throw E.field("kind", "must be in or out");
      let raw = "";
      if (isNum(req.lat) && isNum(req.lng)) {
        if (req.lat < -90 || req.lat > 90 || req.lng < -180 || req.lng > 180) throw E.field("lat", "invalid coordinates");
        raw = `{"lat":${fmtNum(req.lat)},"lng":${fmtNum(req.lng)}}`;
      }
      c.emp(own);
      const today = c.today, tomorrow = addDays(today, 1);
      const last = c.state.punches
        .filter((p) => p.deviceId === "ess" && p.employeeId === own && p.time >= today && p.time < tomorrow)
        .sort((a, b) => (a.time < b.time ? 1 : a.time > b.time ? -1 : 0))[0];
      if (last && last.direction === req.kind) throw E.conflict("already checked " + req.kind);
      const user = String(own);
      const key = dedupeKey("ess", user, c.now, req.kind);
      if (c.state.dedupe.has(key)) throw E.conflict("duplicate punch");
      const p: T.Punch = { id: nextPunchId(c), deviceId: "ess", deviceUserId: user, employeeId: own, time: c.now, direction: req.kind, verify: "unknown", source: "ess", dedupeKey: key, receivedAt: c.now, raw };
      c.state.punches.push(p);
      c.state.dedupe.add(key);
      c.audit(u, "Checked " + req.kind, "Punch", p.id, "self-service");
      return p;
    },
  };
}
